import React from "react";
import { useForm } from "react-hook-form";
import { Box, Button, FormControl, FormLabel, IconButton, InputAdornment, MenuItem, Stack, TextField } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { createCompanyAccount, createCompanyAccountReset, getCompanyList } from "../../state/companyList/companyListSlice";
import { selectCompanyListState } from "../../state/companyList/companyListSelector";

const AddCompanyForm = ({ onClose }) => {
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = React.useState(false);
  const { isAdding, isAddSuccess, perPage, filters } = useSelector(selectCompanyListState);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      companyName: "",
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      lang: "en",
    },
  });

  useEffect(() => {
    if (isAddSuccess) {
      onClose();
      dispatch(getCompanyList({ page: 1, perPage, ...filters }));
      dispatch(createCompanyAccountReset());
    }
  }, [isAddSuccess, onClose, dispatch, perPage, filters]);

  const onSubmit = (data) => {
    dispatch(createCompanyAccount(data));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2}>
          <FormControl fullWidth required error={!!errors.firstName}>
            <FormLabel sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>First Name</FormLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter first name"
              {...register("firstName", { required: "First name is required" })}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
            />
          </FormControl>
          <FormControl fullWidth required error={!!errors.lastName}>
            <FormLabel sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>Last Name</FormLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter last name"
              {...register("lastName", { required: "Last name is required" })}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
            />
          </FormControl>
        </Stack>

        <FormControl fullWidth required error={!!errors.email}>
          <FormLabel sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>Account Email</FormLabel>
          <TextField
            fullWidth
            size="small"
            type="email"
            placeholder="Enter email"
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address",
              },
            })}
            error={!!errors.email}
            helperText={errors.email?.message}
          />
        </FormControl>

        <FormControl fullWidth required error={!!errors.password}>
          <FormLabel sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>Password</FormLabel>
          <TextField
            fullWidth
            size="small"
            type={showPassword ? "text" : "password"}
            placeholder="Enter password"
            {...register("password", {
              required: "Password is required",
              minLength: {
                value: 6,
                message: "Password must be at least 6 characters",
              },
            })}
            error={!!errors.password}
            helperText={errors.password?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </FormControl>

        <FormControl fullWidth required error={!!errors.companyName}>
          <FormLabel sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>Company Name</FormLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter company name"
            {...register("companyName", { required: "Company name is required" })}
            error={!!errors.companyName}
            helperText={errors.companyName?.message}
          />
        </FormControl>

        <FormControl fullWidth required error={!!errors.lang}>
          <FormLabel sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>Preferrable Language</FormLabel>
          <TextField select fullWidth size="small" defaultValue="en" {...register("lang", { required: "Language is required" })} error={!!errors.lang} helperText={errors.lang?.message}>
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="pt">Portuguese</MenuItem>
            <MenuItem value="es">Spanish</MenuItem>
          </TextField>
        </FormControl>

        <Box sx={{ pt: 2 }}>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            size="large"
            disabled={isAdding}
            sx={{
              height: 48,
              textTransform: "none",
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            {isAdding ? "Adding..." : "Add Account"}
          </Button>
        </Box>
      </Stack>
    </form>
  );
};

export default AddCompanyForm;
